import { useEffect, useState } from 'react';
import { CheckCircle, Clock, RotateCw } from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { AiActionButton } from '@/components/ui/AiActionButton';
import { InlineEdit } from '@/components/ui/InlineEdit';
import { InlineSelect } from '@/components/ui/InlineSelect';
import { ChannelIcon } from '@/components/modules/ChannelIcon';
import { useSupportStore } from '@/stores/supportStore';
import { api } from '@/lib/api';
import type { ApiResponse } from '@nexus/shared/types';
import { toast } from '@/stores/toastStore';
import { formatRelative, formatDate } from '@/lib/format';

const statusOptions = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_customer', label: 'Waiting Customer' },
  { value: 'waiting_internal', label: 'Waiting Internal' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

interface TicketSheetProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

export function TicketSheet({ id, open, onClose }: TicketSheetProps) {
  const { selected, fetchOne, update, resolve } = useSupportStore();

  // AI Respond state
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [aiRespondLoading, setAiRespondLoading] = useState(false);

  // AI Priority state
  const [aiPriority, setAiPriority] = useState<{ suggestedPriority: string; reason: string } | null>(null);
  const [aiPriorityLoading, setAiPriorityLoading] = useState(false);

  useEffect(() => {
    if (id && open) {
      fetchOne(id);
      setAiResponse(null);
      setAiPriority(null);
    }
  }, [id, open, fetchOne]);

  const handleAiRespond = async () => {
    if (!id) return;
    setAiRespondLoading(true);
    try {
      const res = await api.post<ApiResponse<{ response: string }>>(`/api/support/tickets/${id}/ai-respond`);
      if (res.data?.response) {
        setAiResponse(res.data.response);
      }
    } catch {
      toast.error('AI Respond failed', 'Check that an AI provider is configured in Settings.');
    } finally {
      setAiRespondLoading(false);
    }
  };

  const handleAiPriority = async () => {
    if (!id) return;
    setAiPriorityLoading(true);
    try {
      const res = await api.post<ApiResponse<{ suggestedPriority: string; reason: string }>>(`/api/support/tickets/${id}/ai-priority`);
      if (res.data) {
        setAiPriority(res.data);
      }
    } catch {
      toast.error('AI Priority failed', 'Check that an AI provider is configured in Settings.');
    } finally {
      setAiPriorityLoading(false);
    }
  };

  const ticket = selected;
  if (!ticket) {
    return (
      <Sheet open={open} onClose={onClose} title="Ticket">
        <div className="flex items-center justify-center py-12 text-surface-500">Loading...</div>
      </Sheet>
    );
  }

  const isResolved = ticket.status === 'resolved' || ticket.status === 'closed';
  const slaRemaining = ticket.slaDeadline
    ? new Date(ticket.slaDeadline).getTime() - Date.now()
    : null;
  const slaBreached = slaRemaining !== null && slaRemaining < 0;

  return (
    <Sheet open={open} onClose={onClose} title="Ticket Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <InlineEdit
            value={ticket.subject}
            onSave={async (v) => { await update(ticket.id, { subject: v }); }}
            className="text-lg font-semibold"
          />
          <div className="flex items-center gap-3">
            <InlineSelect
              value={ticket.status}
              options={statusOptions}
              onSave={async (v) => { await update(ticket.id, { status: v }); }}
            />
            <InlineSelect
              value={ticket.priority}
              options={priorityOptions}
              onSave={async (v) => { await update(ticket.id, { priority: v }); }}
            />
            <AiActionButton
              size="sm"
              label="AI Priority"
              onClick={handleAiPriority}
              loading={aiPriorityLoading}
            />
          </div>
          {aiPriority && (
            <div className="mt-2 p-3 bg-surface-900/80 border border-nexus-600/30 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-nexus-400">AI Suggestion</p>
                <Badge variant="info">{aiPriority.suggestedPriority}</Badge>
              </div>
              <p className="text-xs text-surface-400">{aiPriority.reason}</p>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={async () => {
                    await update(ticket.id, { priority: aiPriority.suggestedPriority });
                    setAiPriority(null);
                  }}
                >
                  Apply
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAiPriority(null)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Details</h3>
          <div className="grid grid-cols-[100px_1fr] gap-y-3 items-center">
            <span className="text-xs text-surface-500">Channel</span>
            <div className="flex items-center gap-1.5">
              <ChannelIcon channel={ticket.channel} />
              <span className="text-sm text-surface-300 capitalize">{ticket.channel.replace('_', ' ')}</span>
            </div>

            <span className="text-xs text-surface-500">Assignee</span>
            <InlineEdit
              value={ticket.assignee || ''}
              placeholder="Unassigned"
              onSave={async (v) => { await update(ticket.id, { assignee: v || null }); }}
            />

            <span className="text-xs text-surface-500">SLA Deadline</span>
            <div className="flex items-center gap-2">
              {ticket.slaDeadline ? (
                <>
                  <Clock className={`h-3.5 w-3.5 ${slaBreached ? 'text-red-400' : 'text-surface-400'}`} />
                  <span className={`text-sm ${slaBreached ? 'text-red-400 font-medium' : 'text-surface-300'}`}>
                    {slaBreached
                      ? 'Breached'
                      : `${Math.floor((slaRemaining ?? 0) / 3_600_000)}h remaining`}
                  </span>
                  <span className="text-xs text-surface-500">({formatDate(ticket.slaDeadline)})</span>
                </>
              ) : (
                <span className="text-sm text-surface-500">No SLA</span>
              )}
            </div>

            <span className="text-xs text-surface-500">Created</span>
            <span className="text-sm text-surface-300">{formatRelative(ticket.createdAt)}</span>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Description</h3>
          <InlineEdit
            value={ticket.description}
            onSave={async (v) => { await update(ticket.id, { description: v }); }}
            multiline
          />
        </div>

        {/* Resolution */}
        {isResolved && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Resolution</h3>
            <InlineEdit
              value={ticket.resolution || ''}
              placeholder="Add resolution notes..."
              onSave={async (v) => { await update(ticket.id, { resolution: v }); }}
              multiline
            />
            {ticket.resolvedAt && (
              <p className="text-xs text-surface-500">Resolved: {formatRelative(ticket.resolvedAt)}</p>
            )}
          </div>
        )}

        {/* AI Suggested Response */}
        {!isResolved && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">AI Response</h3>
              <AiActionButton
                size="sm"
                label={aiResponse ? 'Regenerate' : 'AI Suggest Response'}
                onClick={handleAiRespond}
                loading={aiRespondLoading}
              />
            </div>
            {aiResponse && (
              <div className="p-3 bg-surface-900/80 border border-nexus-600/30 rounded-lg space-y-3">
                <p className="text-sm text-surface-200 whitespace-pre-wrap">{aiResponse}</p>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={async () => {
                      await update(ticket.id, { resolution: aiResponse });
                      toast.success('Response applied to resolution');
                    }}
                  >
                    Use This Response
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<RotateCw className="h-3.5 w-3.5" />}
                    onClick={handleAiRespond}
                    loading={aiRespondLoading}
                  >
                    Regenerate
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {!isResolved && (
          <div className="flex items-center gap-2 pt-2 border-t border-surface-700">
            <Button
              variant="primary"
              size="sm"
              icon={<CheckCircle className="h-3.5 w-3.5" />}
              onClick={async () => { await resolve(ticket.id); }}
            >
              Resolve Ticket
            </Button>
          </div>
        )}
      </div>
    </Sheet>
  );
}
